import {
  BoldPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from "@platejs/basic-nodes/react";
import {
  FontColorPlugin,
  FontFamilyPlugin,
  FontSizePlugin,
  TextAlignPlugin,
} from "@platejs/basic-styles/react";
import { IndentPlugin } from "@platejs/indent/react";
import { LinkPlugin } from "@platejs/link/react";
import { ListPlugin } from "@platejs/list/react";
import { ImagePlugin } from "@platejs/media/react";
import { BlockList } from "@redotech/redo-web/components/plate-ui/block-list";
import { LinkFloatingToolbar } from "@redotech/redo-web/components/plate-ui/link-floating-toolbar";
import { LinkElement } from "@redotech/redo-web/components/plate-ui/link-node";
import { ImageElement } from "@redotech/redo-web/components/plate-ui/media-image-node";
import { KEYS } from "platejs";
import { createElement } from "react";

// Base (non-React) plugins for static HTML serialization
import {
  BaseBoldPlugin,
  BaseItalicPlugin,
  BaseStrikethroughPlugin,
  BaseUnderlinePlugin,
} from "@platejs/basic-nodes";
import {
  BaseFontColorPlugin,
  BaseFontFamilyPlugin,
  BaseFontSizePlugin,
  BaseTextAlignPlugin,
} from "@platejs/basic-styles";
import { BaseIndentPlugin } from "@platejs/indent";
import { BaseLinkPlugin } from "@platejs/link";
import { BaseListPlugin } from "@platejs/list";
import { BaseImagePlugin } from "@platejs/media";

const markPlugins = [
  BoldPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  StrikethroughPlugin,
];

const linkPlugin = LinkPlugin.configure({
  render: {
    node: LinkElement,
    afterEditable: () => createElement(LinkFloatingToolbar),
  },
});

const listTargetPlugins = [KEYS.p];

export const emailEditorPlugins = [
  ...markPlugins,
  FontFamilyPlugin,
  FontSizePlugin,
  FontColorPlugin,
  TextAlignPlugin.configure({
    inject: {
      nodeProps: {
        defaultNodeValue: "start",
        nodeKey: "align",
        styleKey: "textAlign",
        validNodeValues: ["start", "left", "center", "right", "end", "justify"],
      },
      targetPlugins: [KEYS.p],
    },
  }),
  ImagePlugin.withComponent(ImageElement),
  linkPlugin,
  IndentPlugin.configure({ inject: { targetPlugins: listTargetPlugins } }),
  ListPlugin.configure({
    inject: { targetPlugins: listTargetPlugins },
    render: { belowNodes: BlockList },
  }),
];

export const chatEditorPlugins = [...markPlugins];

export const editorToolbarMode = ["email", "chat"] as const;
export type EditorToolbarMode = (typeof editorToolbarMode)[number];

export function getPluginsForMode(mode: EditorToolbarMode) {
  switch (mode) {
    case "email":
      return emailEditorPlugins;
    case "chat":
      return chatEditorPlugins;
  }
}

// Base plugin lists for static HTML serialization
const baseMarkPlugins = [
  BaseBoldPlugin,
  BaseItalicPlugin,
  BaseUnderlinePlugin,
  BaseStrikethroughPlugin,
];

export const emailBasePlugins = [
  ...baseMarkPlugins,
  BaseFontFamilyPlugin,
  BaseFontSizePlugin,
  BaseFontColorPlugin,
  BaseTextAlignPlugin.configure({
    inject: {
      nodeProps: {
        defaultNodeValue: "start",
        nodeKey: "align",
        styleKey: "textAlign",
        validNodeValues: ["start", "left", "center", "right", "end", "justify"],
      },
      targetPlugins: [KEYS.p],
    },
  }),
  BaseImagePlugin,
  BaseLinkPlugin,
  BaseIndentPlugin.configure({ inject: { targetPlugins: [KEYS.p] } }),
  BaseListPlugin.configure({ inject: { targetPlugins: [KEYS.p] } }),
];

export const chatBasePlugins = [...baseMarkPlugins];

export function getBasePluginsForMode(mode: EditorToolbarMode) {
  switch (mode) {
    case "email":
      return emailBasePlugins;
    case "chat":
      return chatBasePlugins;
  }
}
